import { DependencyList, useCallback, useEffect, useState } from "react";

export function debounce(cb: (...args: unknown[]) => void, duration: number) {
    // Store the timer variable.
    let timer: NodeJS.Timeout;
    // This function is given to React.
    return (...args: unknown[]) => {
        // Get rid of the old timer.
        clearTimeout(timer);
        // Set a new timer.
        timer = setTimeout(() => {
            // Instead calling the new function.
            // (with the newer data)
            cb(...args);
        }, duration);
    };
}

export function useDebounceCallback(
    cb: (...args: unknown[]) => void,
    inputs: DependencyList,
    duration = 1000,
) {
    // eslint-disable-next-line
    return useCallback(
        debounce(cb as (...args: unknown[]) => void, duration),
        inputs,
    );
}

export function useDebounce<T>(value: T, delay = 500): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        // Set a timer to update the debounced value after the specified delay
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        // Clean up the timer if the value changes or component unmounts
        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}